<template>
    <div class="replace-list">
        <template v-if="editing">
            <div v-for="(entry, index) in draft" :key="index" class="replace-list__row">
                <div v-for="column in row.columns" :key="column.key" class="replace-list__cell">
                    <small>{{ column.label }}</small>
                    <Textarea
                        v-if="column.json"
                        :model-value="textFor(entry, column)"
                        :placeholder="column.placeholder"
                        rows="2"
                        auto-resize
                        @update:model-value="(text) => setCell(index, column, text)"
                    />
                    <InputText
                        v-else
                        :model-value="textFor(entry, column)"
                        :placeholder="column.placeholder"
                        @update:model-value="(text) => setCell(index, column, text)"
                    />
                </div>
                <Button
                    icon="pi pi-times"
                    severity="secondary"
                    variant="text"
                    rounded
                    size="small"
                    aria-label="Remove this entry"
                    @click="removeRow(index)"
                />
            </div>
        </template>

        <!-- The record rather than the draft. A draft is a form's state, and
             there is no form here to hold one. -->
        <template v-else>
            <div v-for="(entry, index) in held" :key="index" class="replace-list__row replace-list__row--reading">
                <div v-for="column in row.columns" :key="column.key" class="replace-list__cell">
                    <small>{{ column.label }}</small>
                    <span class="replace-list__value">{{ textFor(entry, column) || '—' }}</span>
                </div>
            </div>
        </template>

        <p v-if="shown.length === 0" class="replace-list__empty">
            None. <template v-if="!editing">Edit to add one.</template>
        </p>

        <div v-if="editing" class="replace-list__actions">
            <Button label="Add" icon="pi pi-plus" size="small" severity="secondary" @click="addRow" />
            <Button label="Save list" icon="pi pi-check" size="small" :loading="busy" @click="save" />
        </div>

        <p v-if="invalid" class="replace-list__failure">{{ invalid }}</p>
        <p v-else-if="failure" class="replace-list__failure">
            {{ failureText(failure) }}
        </p>
    </div>
</template>

<script setup>
import Button from 'primevue/button'
import InputText from 'primevue/inputtext'
import Textarea from 'primevue/textarea'
import { computed, ref, watch } from 'vue'

import { failureText } from '@/lib/failureText.js'

// A list the server replaces whole.
//
// Drafted, unlike the member sets — the route takes the entire list, so adding
// one entry means sending the others back with it. A row removed here is not
// removed anywhere until Save, and that is the honest reading of a replace.
const props = defineProps({
    row: { type: Object, required: true },
    record: { type: Object, required: true },
    store: { type: Object, required: true },
    // False draws the list as text. There is no draft to show then, and no Save
    // to press — the boxes belong to the edit that sends the whole list back.
    editing: { type: Boolean, default: false }
})
const emit = defineEmits(['changed'])

const draft = ref([])
const busy = ref(false)
const failure = ref(null)
const invalid = ref('')

const held = computed(() => props.row.read(props.record))
const shown = computed(() => (props.editing ? draft.value : held.value))

// Re-seeded when a different record is opened, and when the boxes appear.
//
// Identity rather than the document's contents: a deep watch fired on every
// refresh the collection made, so changing the Status beside this list threw
// away rows somebody had typed and not yet sent. A refused save keeps what was
// typed; a successful one is re-seeded by the panel remounting this row.
watch(
    [() => props.record._id, () => props.editing],
    function () {
        draft.value = props.row.read(props.record).map((entry) => ({ ...entry }))
        invalid.value = ''
    },
    { immediate: true }
)

function addRow() {
    draft.value = [...draft.value, props.row.blank()]
}

function removeRow(index) {
    draft.value = draft.value.filter((each, at) => at !== index)
}

/** A JSON column is typed as text and parsed on the way out, not on every keystroke. */
function textFor(entry, column) {
    const value = entry[column.key]
    if (column.json !== true) {
        return value ?? ''
    }
    if (typeof value === 'string') {
        return value
    }
    return JSON.stringify(value ?? {})
}

function setCell(index, column, text) {
    const next = draft.value.map((entry) => ({ ...entry }))
    next[index][column.key] = text
    draft.value = next
}

async function save() {
    const list = []
    for (const entry of draft.value) {
        const out = {}
        for (const column of props.row.columns) {
            const raw = entry[column.key]
            if (column.json !== true) {
                out[column.key] = raw
                continue
            }
            try {
                out[column.key] = typeof raw === 'string' ? JSON.parse(raw === '' ? '{}' : raw) : (raw ?? {})
            } catch {
                // Named rather than sent. The server would answer
                // REQUEST_VALIDATION_FAILED about the whole body, which says
                // nothing about which row somebody mistyped.
                invalid.value = `${column.label} is not valid JSON`
                return
            }
        }
        list.push(out)
    }
    invalid.value = ''
    busy.value = true
    failure.value = null
    const ok = await props.row.apply(props.store, props.record._id, list)
    busy.value = false
    // `changed` only on success. The parent bumps a version key on that event
    // and the key remounts this component, which destroys both the `failure`
    // ref that had just caught the refusal and the draft list somebody built.
    if (ok === false) {
        failure.value = props.store.error
        return
    }
    emit('changed')
}
</script>

<style scoped>
.replace-list__row {
    align-items: flex-end;
    border-bottom: 1px solid var(--p-content-border-color);
    display: flex;
    gap: 8px;
    padding: 8px 0;
}

.replace-list__cell {
    display: flex;
    flex: 1 1 140px;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
}

.replace-list__cell small {
    color: var(--p-surface-400);
    font-size: 11px;
}

/* Stacked, not two columns. A claim's properties are a line of JSON and the
   rail is 420 pixels wide — side by side, both cells were too narrow to read. */
.replace-list__row--reading {
    align-items: stretch;
    flex-direction: column;
    gap: 6px;
}

/* The cell basis is a width in the editing row and would be a height here,
   which stood every value in a 140-pixel-tall box of its own. */
.replace-list__row--reading .replace-list__cell {
    flex: 0 0 auto;
}

.replace-list__value {
    font-size: 13px;
    overflow-wrap: anywhere;
}

.replace-list__empty {
    color: var(--p-surface-400);
    font-size: 12px;
    margin: 0 0 8px;
}

.replace-list__actions {
    display: flex;
    gap: 8px;
    margin-top: 8px;
}

.replace-list__failure {
    color: var(--p-status-danger);
    font-size: 12px;
    margin: 6px 0 0;
}
</style>
